import { create } from 'zustand';
import type { ResourceMultiplier } from '@kaeliux/shared';
import { DEFAULT_SUN_AGE_DAYS } from '@kaeliux/game-engine';
import { getUniverse } from '../services/universe.service';

/**
 * Which universe the signed-in player is playing in (issue #53). A player
 * can hold progress in several universes at once, so this is deliberately
 * not part of the user's profile — `UniverseSelection` (shown at every
 * login, before race selection) writes it here, and every gameplay service
 * reads it back via `getSelectedUniverseId()` to stamp requests and scope
 * Firestore reads. localStorage, like Firebase Auth's own session, so a new
 * tab picks up both the user and the universe (issue #310); "present the
 * list on every login" is kept by `authStore` clearing it on logout and on
 * every explicit sign-in.
 */
const SELECTED_UNIVERSE_KEY = 'kaeliux-selected-universe';

function readSelectedUniverseId(): string | null {
    try {
        return localStorage.getItem(SELECTED_UNIVERSE_KEY);
    } catch {
        // TODO: Switch to cookies
        return null;
    }
}

/** localStorage can throw (e.g. Safari private mode) — never let a storage failure crash the caller; the id living only in memory for the rest of the tab's life is an acceptable degradation. */
function writeSelectedUniverseId(universeId: string): void {
    try {
        localStorage.setItem(SELECTED_UNIVERSE_KEY, universeId);
    } catch {
        // TODO: Switch to cookies
        /* best-effort */
    }
}

function clearStoredSelectedUniverseId(): void {
    try {
        localStorage.removeItem(SELECTED_UNIVERSE_KEY);
    } catch {
        // TODO: Switch to cookies
        /* best-effort */
    }
}

interface UniverseStoreState {
    /** The current session's chosen universe, or null before `UniverseSelection` runs. */
    selectedUniverseId: string | null;
    resourceMultiplier: ResourceMultiplier;
    /** Build/research/ship-construction time multiplier — independent of resourceMultiplier. */
    speedMultiplier: number;
    /** Fleet travel time multiplier — independent of the other two; unused until fleets exist. */
    flightMultiplier: number;
    sunAges: Record<string, number[]>;
    /**
     * Fetch a universe's multiplier/sun-age context (throws if it doesn't
     * exist or was never initialized — see `getUniverse`'s doc comment) and
     * store it. Deliberately does NOT touch `selectedUniverseId` — see
     * `commitSelectedUniverse` below for why that's a separate step.
     */
    loadUniverse: (universeId: string) => Promise<void>;
    /**
     * Synchronous commit of an ALREADY-loaded universe id (localStorage +
     * `selectedUniverseId`). Split from `loadUniverse` so `UniverseSelection`
     * can fetch both the universe context AND the per-universe profile
     * (`getUserProfile(uid, universeId)`) first, then call this — and the
     * matching `useAuthStore.setState({ profile })` — back-to-back with no
     * `await` between them. React 18 batches synchronous same-tick updates
     * across stores into one render, so `App.tsx` never sees
     * `selectedUniverseId` truthy while `profile.race` is still stale; doing
     * the localStorage/state write eagerly (as this store used to) let
     * that render happen BEFORE the profile refetch resolved, which flashed
     * `RaceSelection` for a returning player and — because `createPlanet`
     * had no "already has a homeworld here" guard — let it be clicked
     * through into a duplicate homeworld.
     */
    commitSelectedUniverse: (universeId: string) => void;
    /** Drop the stored pick (logout, or a universe that failed to load) so the next attempt re-prompts. */
    clearSelectedUniverse: () => void;
}

export const useUniverseStore = create<UniverseStoreState>(() => ({
    selectedUniverseId: readSelectedUniverseId(),
    resourceMultiplier: 1000,
    speedMultiplier: 1,
    flightMultiplier: 1,
    sunAges: { g1: Array.from({ length: 41 }, (_, i) => i === 0 ? 0 : DEFAULT_SUN_AGE_DAYS) },

    loadUniverse: async (universeId: string) => {
        const universe = await getUniverse(universeId);
        useUniverseStore.setState({
            resourceMultiplier: universe.resourceMultiplier,
            speedMultiplier: universe.speedMultiplier ?? 1,
            flightMultiplier: universe.flightMultiplier ?? 1,
            sunAges: universe.sunAges,
        });
    },

    commitSelectedUniverse: (universeId: string) => {
        writeSelectedUniverseId(universeId);
        useUniverseStore.setState({ selectedUniverseId: universeId });
    },

    clearSelectedUniverse: () => {
        clearStoredSelectedUniverseId();
        useUniverseStore.setState({ selectedUniverseId: null });
    },
}));

/**
 * Read the current session's universe id outside a React component (service
 * layer, not hooks) — every real service that talks to `universes/{u}/...`
 * needs this instead of the old hardcoded `UNIVERSE_ID` constant. Throws
 * rather than silently falling back: every caller only ever runs after
 * `App.tsx`'s gate (`user && !selectedUniverseId` → `UniverseSelection`) has
 * already required a pick, so a missing id here means something upstream
 * skipped that gate, not a legitimate "no universe yet" state to paper over.
 */
export function getSelectedUniverseId(): string {
    const id = useUniverseStore.getState().selectedUniverseId;
    if (!id) throw new Error('No universe selected — this should be unreachable past the UniverseSelection gate');
    return id;
}
